import { MenuItem, Stack } from "@mui/material";
import { Box } from "@mui/system";
import { ReactComponent as IconAttention } from "assets/images/icons/attention.svg";
import { ReactComponent as CalendarPlus } from "assets/images/icons/calendar-plus.svg";
import { ReactComponent as CalendarPlusDisabled } from "assets/images/icons/calendar-plus-disabled.svg";
import { ReactComponent as MemberIcon } from "assets/images/icons/member.svg";
import { Phrases } from "assets/strings/phrases";
import ConfirmationBox from "components/ConfirmationBox";
import MemberListDropdown from "components/Dropdown/MemberListDropdown";
import ScreenLoader from "components/ScreenLoader";
import Tooltip from "components/Tooltip";
import { DATE_FORMAT, GENERIC_MEMBER_STATUS_OPTIONS } from "constants/index";
import { stringToColor } from "helpers/utils";
import { TCAMembersListEntry } from "models/member";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

export const TableItem = (props: {
  data: TCAMembersListEntry;
  followupCallback;
  onUpdateStatus;
  onClickEdit?: Function;
}) => {
  const { data, followupCallback, onUpdateStatus, onClickEdit } = props;
  const [loading, setLoading] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(data.status);

  useEffect(() => {
    if (data) {
      setCurrentStatus(data.status);
    }
  }, [data]);

  const memberStatusOptions = useMemo(() => {
    return GENERIC_MEMBER_STATUS_OPTIONS.map(
      (e, index) =>
        !e.isSelectAll && (
          <MenuItem
            key={`statusddlentry_${data.id}_${index}`}
            // selected={e.value === status}
            value={e.value}
          >
            {e.label}
          </MenuItem>
        )
    );
  }, [data]);

  const [confirmationBoxShown, setConfirmationBoxShown] = useState(false);
  const [confirmationBoxMainTitle, setConfirmationBoxMainTitle] = useState("");
  const [confirmationBoxTitle, setConfirmationBoxTitle] = useState("");
  const [confirmationBoxMessage, setConfirmationBoxMessage] = useState(<></>);
  const [confirmationBoxType, setConfirmationBoxType] = useState<"confirmation" | "alert" | "info">(
    "confirmation"
  );
  const [confirmationBoxOnClose, setConfirmationBoxOnClose] = useState(() => void 0);
  const [confirmationBoxIsLoading, setConfirmationBoxIsLoading] = useState(false);

  const showReclaimIdPopup = (id) => {
    setConfirmationBoxMainTitle(Phrases.tcaNewIdNeededTitle);
    setConfirmationBoxTitle(Phrases.tcaNewIdNeededMessage);
    setConfirmationBoxMessage(
      <Box mt={2} mx={2}>
        <ul>
          <li>
            {Phrases.tcaNewIdNeededYes}
            <br />
            <br />
          </li>
          <li>{Phrases.tcaNewIdNeededNo}</li>
        </ul>
      </Box>
    );
    setConfirmationBoxOnClose((_prev, _props) => {
      return (v) => {
        if (typeof v !== "boolean") {
          setConfirmationBoxShown(false);
        }
        if (v) {
          //Positive response
          onClickEdit(id);
          setConfirmationBoxShown(false);
        } else {
          setConfirmationBoxShown(false);
        }
      };
    });
    setConfirmationBoxShown(true);
  };

  console.log(`currentStatus is ${currentStatus}`);

  return (
    <>
      <div className={"member-card"}>
        <span
          style={{ backgroundColor: stringToColor(`${data.first_name} ${data.last_name}`) }}
          className={"rounded-circle member-icon"}
        >
          <MemberIcon width={24} height={24} stroke={"#fff"} />
        </span>
        <div className="name">
          <h3>{data.last_name}</h3>
        </div>
        <div className="name">
          <h3>{data.first_name}</h3>
        </div>
        <div className="last-workout-session">
          <h3 className={"grey"}>{data.next_appointment}</h3>
        </div>
        <div className="last-workout-session">
          <Stack direction="row" spacing={0.25} alignItems="center">
            <h3 className={"grey"}>{moment(data.next_appointment_date).format(DATE_FORMAT)}</h3>
            {data.last_appointment_75_days_ago_flag && <IconAttention />}
          </Stack>
        </div>
        <div className="fitlinxx-id">
          <h3 className={"grey"}>{data.fitlinxx_id.length > 5 ? "------" : data.fitlinxx_id}</h3>
        </div>
        <span className="status-container">
          {loading && <ScreenLoader isScreenLoader />}
          {!loading && (
            <MemberListDropdown
              value={currentStatus}
              // defaultValue={currentStatus ?? ""}
              onChange={(v) => {
                if (data.fitlinxx_id.length > 5 && currentStatus.match("TERMINATED")) {
                  showReclaimIdPopup(data.id);
                } else {
                  setLoading(true);
                  onUpdateStatus(data.id, v.target.value).then((_e) => {
                    setCurrentStatus(v.target.value.toString());
                    setLoading(false);
                  });
                }
              }}
              fullWidth={true}
              style={{ marginRight: 10 }}
              className={`select primary`}
            >
              {memberStatusOptions}
            </MemberListDropdown>
          )}
        </span>
        <div>
          <div className={"buttons-bar"}>
            {!["TERMINATED_FACILITY", "TERMINATED", "IN_ACTIVE"].includes(currentStatus) &&
              data.can_proceed_with_next_follow_up && (
                <Tooltip content={"Follow Up Appointment"} direction="left">
                  <button
                    className={"primary-icon"}
                    type="button"
                    onClick={() => followupCallback(data)}
                  >
                    <CalendarPlus />
                  </button>
                </Tooltip>
              )}
            {(["TERMINATED_FACILITY", "TERMINATED", "IN_ACTIVE"].includes(currentStatus) ||
              !data.can_proceed_with_next_follow_up) && (
              <Tooltip
                content={
                  !data.can_proceed_with_next_follow_up
                    ? "Can't have a follow up on the same day"
                    : "IN_ACTIVE".includes(currentStatus)
                    ? "Member Inactive"
                    : "Member Terminated"
                }
                direction={!data.can_proceed_with_next_follow_up ? "left" : "bottom"}
              >
                <button className={"primary-icon"} type="button" disabled>
                  <CalendarPlusDisabled />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      <ConfirmationBox
        open={confirmationBoxShown}
        onClose={confirmationBoxOnClose}
        mainTitle={confirmationBoxMainTitle}
        title={confirmationBoxTitle}
        message={confirmationBoxMessage}
        type={confirmationBoxType}
        isLoading={confirmationBoxIsLoading}
      />
    </>
  );
};
